import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { useSetAtom } from 'jotai';
import { PackageCompleteness } from '../../../types/packager-types';
import { completenessModalPackageIdAtom } from '../../../atoms/packager-atoms';

interface ICompletenessChipProps {
  // undefined (field omitted by older API builds) is treated the same as null.
  completeness?: PackageCompleteness | null;
  packageId: string;
}

// Tier-1 "is this package OK?" glance. Driven ONLY by completeness.health — the
// client never re-derives health. completeness === null is a calm "not recorded"
// state (legacy packages), never an error.
const CompletenessChip: React.FC<ICompletenessChipProps> = ({ completeness, packageId }) => {
  const setCompletenessModalPackageId = useSetAtom(completenessModalPackageIdAtom);

  const handleClick = () => {
    setCompletenessModalPackageId(packageId);
  };

  let color: 'success' | 'error' | 'default';
  let label: string;
  let tooltip: string;

  if (!completeness) {
    color = 'default';
    label = '—';
    tooltip = 'Not recorded for this package.';
  } else if (completeness.health === 'COMPLETE') {
    color = 'success';
    label = 'Complete';
    tooltip = 'Everything we kept was delivered.';
  } else {
    const lost = completeness.documents_to_deliver - completeness.documents_delivered;
    color = 'error';
    label = 'Incomplete';
    tooltip = `${lost.toLocaleString()} ${
      lost === 1 ? 'document was' : 'documents were'
    } kept but didn't make it into the file.`;
  }

  return (
    <Tooltip title={tooltip} placement="top">
      <Chip
        label={label}
        color={color}
        size="small"
        onClick={handleClick}
        data-cy={`chip-completeness-${packageId}`}
        sx={{ cursor: 'pointer' }}
      />
    </Tooltip>
  );
};

export default CompletenessChip;
